<template>
  <div style="border-top: 2px solid transparent">
    <h3
      class="my-0 t-600 d-flex align-items-center"
      :class="{
        'bg-transparent t-regular': isFilterSet,
        'bg-filter-grey t-regular': !isFilterSet && !hasActiveBuckets,
        'bg-blue t-white': !isFilterSet && hasActiveBuckets
      }"
      style="border-bottom: 2px"

      v-tooltip.top-start="{
        content: filter.tooltip,
        delay: 200,
        container: 'body',
        boundariesElement: 'body'
      }"
    >
      <div v-if="isFilterSet"
        class="bg-white d-flex flex-grow-1 align-items-center t-sm px-3 py-2"
        style="margin-bottom: 2px"
      >
        <i class="fa fa-magic t-secondary width--20px" />
        <span class="flex-grow-1">
          {{ filterLabel }}
        </span>
      </div>
      <div
        v-else
        class="d-flex flex-grow-1 align-items-center t-sm px-3 py-2 is-hover"
        @click.prevent="canViewFilters ? collapseFilter(filterName, filter) : $can.notAllowed()"
      >
        <i
          class="fa width--20px"
          :class="{
            'is-hover': canViewFilters,
            'fa-caret-down': !filter.collapsed,
            'fa-caret-right': filter.collapsed,
            't-secondary': !hasActiveBuckets,
          }"
        />
        <span class="flex-grow-1">
          {{ filterLabel }}
          <i class="fa fa-lock t-secondary ml-2" v-if="!canViewFilters" />
          <i v-if="filter.test_filter" v-tooltip="'Filter only shown in staging'" class="fa fa-battery-2 t-yellow pl-1 t-sm" />
        </span>
        <span v-if="filter.new" class="label-base label-filter-header bg-yellow">NEW</span>
      </div>

      <typeahead v-if="filter.typeahead && !isMobileFilter"
        :remote="filterName"
        :name="filterName"
        :isElasticSearch="true"
        :minLength="2"
        :elasticState="state"
        :displayKey="`label`"
        :placeholder="''"
        :customClass="`pr-2 width--100px typeahead-in-filter-header position-relative`"
        :customInputClass="'form-control-sm'"
        attachedToBody
        @selected="handleTypeahead">
      </typeahead>
    </h3>

    <!-- filter buckets -->
    <section class="bg-white" :class="{'highlight-overlay': isFilterLoaded && isHighlighted}">
      <div
        v-show="!filter.collapsed && !filter.isLoading"
        v-if="!isEmpty(filter.buckets)"
        class="t-sm"
      >
        <a
          v-if="filter.browse"
          @click.stop
          @click.prevent="openFilterModal(filterName)"
          href="#"
          class="px-3 t-400 d-flex align-items-center pt-1"
        >
          <i class="fa fa-list-alt mr-2 t-secondary" />
          {{ getFilterModalCopy(filterName) }}
          <i class="fa fa-lock t-secondary ml-2" v-if="!canUseFilters" />
        </a>
        <a
          v-if="isCountryFilter"
          @click.stop
          @click.prevent="openMapModal"
          href="#"
          class="px-3 t-400 d-flex align-items-center pt-1"
        >
          <i class="fa fa-globe mr-2 t-secondary" />
          See countries on world map
          <i class="fa fa-lock t-secondary ml-2" v-if="!$can('viewMapModal')" />
        </a>
        <a
          v-if="isUSStateFilter"
          @click.stop
          @click.prevent="openMapModal"
          href="#"
          class="px-3 t-400 d-flex align-items-center pt-1"
        >
          <i class="fa fa-globe mr-2 t-secondary" />
          See states on US map
          <i class="fa fa-lock t-secondary ml-2" v-if="!$can('viewMapModal')" />
        </a>
        <a
          v-if="filter.trend"
          @click.stop
          @click.prevent="openTrendsModal"
          href="#"
          class="px-3 t-400 d-flex align-items-center justify-content-between pt-1"
        >
          <span class="d-flex align-items-center">
            <i class="fa fa-line-chart mr-2 t-secondary" />
            Show trends
            <i class="fa fa-lock t-secondary ml-2" v-if="!$can('viewTrendsModal')" />
          </span>
        </a>
        <div @click="!canUseFilters && $can.notAllowed()">
          <bucket-set v-if="filter.type === 'set'"
            :filterName="filterName"
            :isFilterLoaded="isFilterSetLoaded"
            :filter="filter"
            :state="state"
            :isLocked="!canUseFilters"
            @setToggled="handleSetToggled">
          </bucket-set>

          <bucket-text v-if="filter.type === 'text' || filter.type === 'missing'"
            :filterName="filterName"
            :isFilterLoaded="isFilterLoaded"
            :filter="filter"
            :state="state"
            :isLocked="!canUseFilters"
            :renderFlag="isCountryFilter"
            @bucketToggled="handleBucketToggled"
            @bucketShowAll="handleBucketShowAll">
          </bucket-text>

          <bucket-range v-else-if="filter.type === 'number_range' || filter.type === 'date_range'"
            :filterName="filterName"
            :isFilterLoaded="isFilterLoaded"
            :filter="filter"
            :isLocked="!canUseFilters"
            @bucketToggled="handleBucketToggled">
          </bucket-range>

          <bucket-since v-else-if="filter.type === 'since'"
            :isFilterLoaded="isFilterLoaded"
            :filterName="filterName"
            :filter="filter"
            :index="state.index"
            :isMobileFilter="isMobileFilter"
            :isLocked="!canUseFilters"
            @bucketSinceToggled="handleBucketSinceToggled">
          </bucket-since>

          <bucket-date v-else-if="filter.type === 'date'"
            :filterName="filterName"
            :filter="filter"
            :isLocked="!canUseFilters"
            @bucketDateToggled="handleBucketDateToggled">
          </bucket-date>
        </div>
      </div>
      <!-- non active -->
      <p v-if="!isFilterSet && !filter.collapsed && isEmpty(filter.buckets) && !filter.isLoading" class="t-secondary mb-0 t-sm px-3 py-1">
        No active filters for your query.
      </p>

      <!-- loading -->
      <spinner
        v-if="!filter.collapsed && (filter.isLoading || (isFilterSet && !isFilterSetLoaded))"
        :containerClass="`width--100px min-height--100px px-3`" />
    </section>
  </div>
</template>

<script>
import BucketSet from '@/components/search/filter/bucket/Set'
import BucketText from '@/components/search/filter/bucket/Text'
import BucketDate from '@/components/search/filter/bucket/Date'
import BucketSince from '@/components/search/filter/bucket/Since'
import BucketRange from '@/components/search/filter/bucket/Range'
import Spinner from '@/components/shared/Spinner'
import Typeahead from '@/components/shared/form/Typeahead'

export default {
  name: 'searchFilter',
  components: {
    BucketSince,
    BucketText,
    BucketDate,
    BucketRange,
    BucketSet,
    Spinner,
    Typeahead
  },
  props: {
    canUseFilters: {
      type: Boolean,
      default: false
    },
    canViewFilters: {
      type: Boolean,
      default: false
    },
    isFilterLoaded: {
      type: Boolean,
      default: false
    },
    isFilterSetLoaded: {
      type: Boolean,
      default: false
    },
    filterName: {
      type: String,
      required: true
    },
    filter: {
      type: Object,
      required: true
    },
    state: {
      type: Object,
      required: true
    },
    isMobileFilter: {
      type: Boolean,
      default: false
    },
    isHighlighted: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    filterLabel () {
      return this.filter.label ? this.filter.label : this.$options.filters.humanize(this.filterName)
    },
    isFilterSet () {
      return this.filterName === 'quick_filters'
    },
    hasActiveBuckets () {
      if (!this.filter || !this.filter.buckets) return false
      return this.filter.buckets.some(bucket => bucket.active)
    },
    isCountryFilter () {
      return (this.filter.map_type && this.filter.map_type === 'countries') || false
    },
    isUSStateFilter () {
      return (this.filter.map_type && this.filter.map_type === 'US_states') || false
    }
  },
  methods: {
    getFilterModalCopy (filterName) {
      if (filterName === 'countries' || this.isCountryFilter) {
        return 'See countries per continent'
      } else if (filterName === 'organisations') {
        return 'See organisations per country'
      }
      return 'View all'
    },
    handleTypeahead (filterName, item) {
      let bucketValue = !this.isEmpty(item.value) ? item.value : item
      let bucketLabel = !this.isEmpty(item.label) ? item.label : item

      // is selected bucket present?
      if (this.filter.buckets) {
        for (let bucket of this.filter.buckets) {
          if (bucket.value === bucketValue) {
            bucket.active = !bucket.active
            return this.$emit('bucketToggled', bucket, filterName)
          }
        }
      }
      // If selected bucket is not present, we add a new bucket
      this.$emit('bucketAdded', { active: true, value: bucketValue, count: item.count, label: bucketLabel }, filterName)
    },
    collapseFilter (filterName, filter) {
      this.$emit('filterCollapseToggled', filterName, filter)
    },
    handleBucketToggled (bucket, filterName) {
      this.$emit('bucketToggled', bucket, filterName)
    },
    handleSetToggled (bucket) {
      this.$emit('setToggled', bucket)
    },
    handleBucketSinceToggled (bucket, filterName) {
      this.$emit('bucketSinceToggled', bucket, filterName)
    },
    handleBucketDateToggled (bucket, filterName) {
      this.$emit('bucketDateToggled', bucket, filterName)
    },
    handleBucketShowAll (filterName, filter) {
      this.$emit('bucketShowAll', filterName, filter)
    },
    openFilterModal (filterName) {
      if (!this.canUseFilters) {
        this.$can.notAllowed()
      } else {
        this.$events.$emit('modal:filter:browse:open', filterName, this.state)
      }
    },
    openMapModal () {
      if (!this.$can('viewMapModal')) {
        this.$can.notAllowed(this.filter.map_type)
      } else {
        this.$events.$emit('modal:filter:map:open', this.filterName, this.state, this.filter.map_type)
      }
    },
    openTrendsModal () {
      if (!this.$can('viewTrendsModal')) {
        this.$can.notAllowed()
      } else {
        this.$events.$emit('modal:filter:trends:open', this.filterName, this.state, this.filter.trend)
      }
    }
  }
}
</script>
